import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { useNavigate } from 'react-router-dom';
import createIcon from '../../asset/icon/create.png';
import minusIcon from '../../asset/icon/minus.png';
import linkIcon from '../../asset/icon/link.png';
import linkCutIcon from '../../asset/icon/linkCut.png';
import deleteOpenIcon from '../../asset/icon/delete_open.png';
import deleteCloseIcon from '../../asset/icon/delete_close.png';
import orangeIcon from '../../asset/icon/orange.png';
import UniversalIconBtn from '../../shared/component/headless/button/UniversalIconBtn';
import { imHere } from '../../shared/component/Animation';
import HintWrapper from '../../shared/component/headless/button/HintWrapper';
import { PIInput } from '../../shared/component/headless/input/InputBody';
import MoleculeInputDiv from '../../shared/component/molecule/MoleculeInputDiv';
import { getHintMessage } from '../../shared/constants/hintMessages';
import { getAlertMessage } from '../../shared/constants/alertMessages';
import { PROJECT_INFO_VALUE_MAX_LENGTH } from '../../shared/constants/limits';
import { ProjectInfoData, ProjectInfoValueData, SimpleVenueData } from '../../shared/dto/EntityRepository';
import { AlertPosition, AlertType, ModalType, ProjectInfoType } from '../../shared/enum/EnumRepository';
import { useEditMode } from '../../shared/hooks/useEditMode';
import { useModalStore } from '../../shared/store/portal/modalStore';
import { useStandardAlertStore } from '../../shared/store/portal/alertStore';
import { useProjectInfoListStore } from '../../shared/store/projectInfoStore';
import { getProjectInfoValuePlaceholder } from '../../shared/util/getProjectInfoTypeLabel';
import { openNewTab } from '../../shared/util/openNewTab';
import { parseHttpUrl } from '../../shared/util/urlValidation';
import {
  appendProjectInfoValue,
  createEmptyProjectInfoValue,
  normalizeProjectInfoType,
  patchProjectInfoValueAt,
  removeProjectInfoValueAt,
  moveProjectInfoValue,
} from '../../shared/util/projectInfo';
import ProjectInfoDateInput from './ProjectInfoDateInput';
import ProjectInfoLinkedInput from './ProjectInfoLinkedInput';

interface ProjectInfoValuesProps {
  data: ProjectInfoData;
  onDelete?: () => void;
  onChange?: (updates: Partial<ProjectInfoData>) => void;
  canManageLinks?: boolean;
  canReorderValues?: boolean;
}

const ProjectInfoValues: React.FC<ProjectInfoValuesProps> = ({
  data,
  onDelete,
  onChange,
  canManageLinks = true,
  canReorderValues = false,
}) => {
  const { isEditMode } = useEditMode();
  const updateProjectInfo = useProjectInfoListStore((s) => s.updateProjectInfo);
  const { setStandardModal } = useModalStore();
  const enqueueAlert = useStandardAlertStore((s) => s.enqueueAlert);
  const navigate = useNavigate();
  const projectInfoType = normalizeProjectInfoType(data.type);
  const isActiveDate = projectInfoType === ProjectInfoType.ACTIVE_DATE;
  const isLocation = projectInfoType === ProjectInfoType.LOCATION;
  const canLinkUrl = projectInfoType === ProjectInfoType.CREDIT || projectInfoType === ProjectInfoType.OTHER;
  const showButtons = isEditMode && (!isActiveDate || Boolean(onDelete));
  const values = data.customValues.length > 0 ? data.customValues : [createEmptyProjectInfoValue()];
  const visibleValues = isActiveDate ? values.slice(0, 1) : values;
  const showReorderHandles = canReorderValues && visibleValues.length > 1;
  const buttonSlots = Number(!isActiveDate)
    + Number(canManageLinks && (isLocation || canLinkUrl))
    + Number(Boolean(onDelete));
  const valueRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const addHint = getHintMessage('ADD_INFO_VALUE');
  const removeHint = getHintMessage('REMOVE_INFO_VALUE');
  const deleteHint = getHintMessage('DELETE_INFO');

  const handleChange = (updates: Partial<ProjectInfoData>) => {
    if (onChange) {
      onChange(updates);
      return;
    }
    updateProjectInfo(data.id, updates);
  };

  const clearValueDrag = () => {
    setDraggingIndex(null);
    setDragOverIndex(null);
  };

  const handleValueDragStart = (
    event: React.PointerEvent<HTMLButtonElement>,
    index: number,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    setDraggingIndex(index);
    setDragOverIndex(index);
  };

  const handleValueDragMove = (event: React.PointerEvent<HTMLButtonElement>) => {
    if (draggingIndex === null) return;
    event.preventDefault();

    const pointerY = event.clientY;
    let targetIndex = visibleValues.length;
    for (let index = 0; index < visibleValues.length; index += 1) {
      const rect = valueRefs.current[index]?.getBoundingClientRect();
      if (rect && pointerY < rect.top + rect.height / 2) {
        targetIndex = index;
        break;
      }
    }
    setDragOverIndex(targetIndex);
  };

  const handleValueDragEnd = () => {
    if (draggingIndex !== null && dragOverIndex !== null) {
      const targetIndex = dragOverIndex > draggingIndex
        ? dragOverIndex - 1
        : dragOverIndex;

      if (draggingIndex === targetIndex) {
        clearValueDrag();
        return;
      }

      handleChange({
        customValues: moveProjectInfoValue(values, draggingIndex, targetIndex),
      });
    }
    clearValueDrag();
  };

  const handleMoveToUrl = (value: string | null) => {
    if (isEditMode || !value) return;

    const url = parseHttpUrl(value);
    if (!url) {
      enqueueAlert({
        type: AlertType.ALERT,
        position: AlertPosition.TOP,
        content: getAlertMessage('INVALID_URL_FORMAT'),
      });
      return;
    }

    const isArchitraveUrl =
      url.hostname === 'architrave.work' ||
      url.hostname.endsWith('.architrave.work');

    if (isArchitraveUrl) {
      navigate(`${url.pathname}${url.search}${url.hash}`);
      return;
    }

    enqueueAlert({
      type: AlertType.CONFIRM,
      position: AlertPosition.TOP,
      content: getAlertMessage('CONFIRM_MOVE_TO_CAREER_RELATED_URL'),
      callBack: () => openNewTab(url.href),
    });
  };

  const handleValueChange = (index: number, updates: Partial<ProjectInfoValueData>) => {
    handleChange({
      customValues: patchProjectInfoValueAt(projectInfoType, values, index, updates),
    });
  };

  const handleVenueChange = (index: number, venue: SimpleVenueData | null) => {
    handleValueChange(index, {
      venueId: venue ? Number(venue.id) : null,
      ...(venue && { text: venue.name.trim() || venue.nameEn.trim() || venue.venueCode }),
    });
  };

  const openVenueSearchModal = (index: number) => {
    setStandardModal({
      modalType: ModalType.PROJECT_INFO_VENUE_SEARCH,
      title: null,
      value: data.id,
      handleChange: (_value, payload) => {
        if (!payload) return;
        handleVenueChange(index, payload as SimpleVenueData);
      },
    });
  };

  const openUrlModal = (index: number) => {
    setStandardModal({
      modalType: ModalType.PROJECT_INFO_URL,
      title: null,
      value: values[index].url ?? '',
      handleChange: (url) => {
        if (url === (values[index].url ?? '')) return;
        handleValueChange(index, { url: url || null });
      },
    });
  };

  return (
    <ValueList
      $showReorderHandles={showReorderHandles}
    >
      {visibleValues.map((value, index) => {
        const isLinked = isLocation ? value.venueId != null : Boolean(value.url);
        const canMoveToUrl = !isEditMode && Boolean(value.url);
        const linkHint = getHintMessage(isLocation
          ? (isLinked ? 'DISCONNECT_VENUE' : 'CONNECT_VENUE')
          : (isLinked ? 'EDIT_URL' : 'CONNECT_URL'));
        return (
          <React.Fragment key={index}>
            {showReorderHandles && (
              <ValueDropSpace
                $isActive={draggingIndex !== null && dragOverIndex === index}
              />
            )}
            <ValueRow
              ref={(element) => { valueRefs.current[index] = element; }}
              $isDragging={draggingIndex === index}
            >
              {showReorderHandles && (
                <ValueDragHandle
                  type="button"
                  aria-label={`Move value ${index + 1}`}
                  $isDragging={draggingIndex === index}
                  onPointerDown={(event) => handleValueDragStart(event, index)}
                  onPointerMove={handleValueDragMove}
                  onPointerUp={handleValueDragEnd}
                  onPointerCancel={clearValueDrag}
                >
                  ::
                </ValueDragHandle>
              )}
              <ValueWrapper
                $canMoveToUrl={canMoveToUrl}
                onClick={() => handleMoveToUrl(value.url)}
                role={canMoveToUrl ? 'link' : undefined}
                tabIndex={canMoveToUrl ? 0 : undefined}
                onKeyDown={(event) => {
                  if (canMoveToUrl && event.key === 'Enter') handleMoveToUrl(value.url);
                }}
              >
                {isEditMode && isActiveDate ? (
                  <ProjectInfoDateInput
                    value={value.text}
                    onChange={(text) => handleValueChange(index, { text })}
                  />
                ) : isEditMode && isLinked ? (
                  <ProjectInfoLinkedInput
                    value={value.text}
                    placeholder={getProjectInfoValuePlaceholder(projectInfoType)}
                    onChange={(text) => handleValueChange(index, { text })}
                    venueId={isLocation ? value.venueId : null}
                    url={value.url}
                  />
                ) : (
                  <MoleculeInputDiv
                    value={value.text}
                    placeholder={getProjectInfoValuePlaceholder(projectInfoType)}
                    handleChange={(event) => handleValueChange(index, { text: event.target.value })}
                    inputStyle={PIInput}
                    StyledDiv={canMoveToUrl ? LinkedValueSection : ValueSection}
                    maxLength={PROJECT_INFO_VALUE_MAX_LENGTH}
                  />
                )}
              </ValueWrapper>
              {showButtons && (
                <BtnContainer $slots={buttonSlots}>
                  {!isActiveDate && (
                    <HintWrapper content={index === 0 ? addHint : removeHint}>
                      <UniversalIconBtn
                        size="fit"
                        border={false}
                        backgroundImg={index === 0 ? createIcon : minusIcon}
                        onClick={() => handleChange({
                          customValues: index === 0
                            ? appendProjectInfoValue(values)
                            : removeProjectInfoValueAt(values, index),
                        })}
                        aria-label={index === 0 ? addHint : removeHint}
                      />
                    </HintWrapper>
                  )}
                  {canManageLinks && (isLocation || canLinkUrl) && (
                    <HintWrapper content={linkHint}>
                      <UniversalIconBtn
                        size="fit"
                        border={false}
                        backgroundImg={isLinked ? linkCutIcon : linkIcon}
                        onClick={() => {
                          if (isLocation) {
                            if (isLinked) handleVenueChange(index, null);
                            else openVenueSearchModal(index);
                          } else {
                            openUrlModal(index);
                          }
                        }}
                        aria-label={linkHint}
                        aria-pressed={isLinked}
                      />
                    </HintWrapper>
                  )}
                  {index === 0 && onDelete && (
                    <>
                      <HintWrapper content={deleteHint} toLeft={true}>
                        <UniversalIconBtn
                          size="fit"
                          border={false}
                          backgroundImg={deleteCloseIcon}
                          backgroundImgHover={deleteOpenIcon}
                          onClick={onDelete}
                          aria-label={deleteHint}
                        />
                      </HintWrapper>
                      {data.hasChanged && (
                        <ChangedIconWrap>
                          <ChangedIcon src={orangeIcon} alt="Unsaved changes" />
                        </ChangedIconWrap>
                      )}
                    </>
                  )}
                </BtnContainer>
              )}
            </ValueRow>
          </React.Fragment>
        );
      })}
      {showReorderHandles && (
        <ValueDropSpace
          $isActive={draggingIndex !== null && dragOverIndex === visibleValues.length}
        />
      )}
    </ValueList>
  );
};

export default ProjectInfoValues;

const ValueList = styled.div<{
  $showReorderHandles: boolean;
}>`
  display: flex;
  flex-direction: column;
  gap: ${({ $showReorderHandles }) => $showReorderHandles ? '0' : '4px'};
  flex: 1;
  min-width: 0;
`;

const ValueRow = styled.div<{
  $isDragging: boolean;
}>`
  display: flex;
  align-items: start;
  gap: clamp(12px, 1vw, 14px);
  width: 100%;
  min-width: 0;
  opacity: ${({ $isDragging }) => $isDragging ? 0.45 : 1};
  transform: ${({ $isDragging }) =>
    $isDragging ? 'scaleX(0.98) scaleY(0.45)' : 'none'};
  transition: all 0.25s ease-out;
`;

const ValueDragHandle = styled.button<{
  $isDragging: boolean;
}>`
  align-self: start;
  margin-top: 4px;
  border: none;
  background: transparent;
  padding: 6px 2px;
  color: ${({ theme, $isDragging }) =>
    $isDragging
      ? theme.colors.color_Gray_03
      : theme.colors.color_Gray_04};
  ${({ theme }) => theme.typography.Body_03_1};
  cursor: grab;
  touch-action: none;
  user-select: none;

  &:active {
    cursor: grabbing;
  }
`;

const ValueWrapper = styled.div<{ $canMoveToUrl: boolean }>`
  flex: 1;
  min-width: 0;
  cursor: ${({ $canMoveToUrl }) => $canMoveToUrl ? 'pointer' : 'default'};
`;

const ValueSection = styled.div`
  display: flex;
  align-items: center;
  padding: 5px 0px;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.color_Gray_03};
  ${({ theme }) => theme.typography.Body_03_1};
`;

const LinkedValueSection = styled(ValueSection)`
  text-underline-offset: 3px;

  &:hover {
    text-decoration: underline;
  }
`;

const BtnContainer = styled.div<{ $slots: number }>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  justify-content: flex-start;
  width: ${({ $slots }) => `${$slots * 26 + Math.max(0, $slots - 1) * 4}px`};
  padding: 4px 0px;
`;

const ChangedIconWrap = styled.div`
  position: absolute;
  left: 100%;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  ${imHere}
`;

const ChangedIcon = styled.img`
  width: 12px;
  height: 12px;
  margin: 4px;
`;

const ValueDropSpace = styled.div<{ $isActive: boolean }>`
  width: 100%;
  height: ${({ $isActive }) => $isActive ? '32px' : '4px'};
  border: ${({ $isActive, theme }) =>
    $isActive ? `1px dashed ${theme.colors.color_Gray_05}` : 'none'};
  border-radius: 2px;
  transition: all 0.25s ease-out;
`;
